import { CommonConstants } from "@/common/CommonConstants";
import { CommonButton } from "@/components/common/buttons/CommonButton";
import { DriverWorkCycleDetailTab } from "@/components/driverWorkCycles/DriverWokCycleDetailTab";
import { DriverWorkShiftDetailTab } from "@/components/driverWorkCycles/DriverWorkShiftDetailTab";
import Title from "@/components/molecules/Title";
import { DriverWorkCycle } from "@/models/common";
import {
  CycleTab,
  DriverWorkCyclesState,
  ShiftTab,
  Tabs as TabType,
} from "@/models/driverWorkShiftsAndCycle";
import { Office } from "@/models/office";
import { DriverWorkShift } from "@/models/operationPlans";
import { getDriverWorkCyclesAPI, getOfficeAPI } from "@/util/api/API";
import { getDriverWorkShiftsAPI } from "@/util/api/driverWorkShifts";
import { useStatefulNavigator } from "@/util/useStatefulNavigator";
import {
  Grid,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tabs,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { DriverWorkCyclesConstants } from "./DriverWorkCyclesConstants";

export const DriverWorkCycleDetails = () => {
  const params = useParams<{ office_id: string }>();
  const officeId = params.office_id;

  const [office, setOffice] = useState<Office>();
  const [cycles, setCycles] = useState<DriverWorkCycle[]>([]);
  const [shifts, setShifts] = useState<DriverWorkShift[]>([]);

  const [activeTab, setActiveTab] = useState<TabType>(ShiftTab);

  const { navigate, savedState } =
    useStatefulNavigator<DriverWorkCyclesState>();

  const navigateWrapper = useCallback(
    (path: string) => {
      if (savedState == null) {
        navigate(path);
        return;
      }

      navigate(path, {
        ...savedState,
        tab: activeTab,
      });
    },
    [activeTab, navigate, savedState]
  );

  const handleReturn = useCallback(
    () => navigateWrapper("/driver_work_cycles"),
    [navigateWrapper]
  );

  const handleEditCycle = useCallback(
    (cycleId: number) =>
      navigateWrapper(`/driver_work_cycles/${officeId}/cycle/${cycleId}/edit`),
    [navigateWrapper, officeId]
  );

  const handleCreateCycle = useCallback(
    () => navigateWrapper(`/driver_work_cycles/${officeId}/cycle/create`),
    [navigateWrapper, officeId]
  );

  const handleEditShift = useCallback(
    (shiftId: number) =>
      navigateWrapper(`/driver_work_cycles/${officeId}/shift/${shiftId}/edit`),
    [navigateWrapper, officeId]
  );

  const handleCreateShift = useCallback(
    () => navigateWrapper(`/driver_work_cycles/${officeId}/shift/create`),
    [navigateWrapper, officeId]
  );

  const handleChangeTab = useCallback(
    (event: React.SyntheticEvent<Element, Event>, tab: any) => {
      setActiveTab(tab);
    },
    []
  );

  useEffect(() => {
    if (officeId == null) {
      return;
    }

    getOfficeAPI(Number(officeId))
      .then((res) => setOffice(res.data))
      .catch((e) =>
        console.log("===Error occurred while getting Office===", e)
      );

    getDriverWorkCyclesAPI([Number(officeId)])
      .then((res) => setCycles(res.data))
      .catch((e) =>
        console.log("===Error occurred while getting WorkCycles===", e)
      );

    getDriverWorkShiftsAPI(Number(officeId))
      .then((res) => setShifts(res.data))
      .catch((e) =>
        console.log("===Error occurred while getting WorkShifts===", e)
      );

    if (savedState?.tab != null) {
      setActiveTab(savedState.tab);
    }
  }, [officeId, savedState?.tab]);

  return (
    <>
      <Grid
        container
        spacing={2}
        sx={{ paddingLeft: 2, paddingRight: 2, mt: 3 }}
      >
        <Grid container spacing={2} sx={{ display: "flex", pl: 2 }}>
          <Title style={{ flexGrow: 1 }}>
            {DriverWorkCyclesConstants.DriverWorkCycleAndShiftsDetail}
          </Title>
          <CommonButton
            btnType="Return"
            sx={{
              height: 35,
              mr: 1,
            }}
            onClickBtn={handleReturn}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2} sx={{ paddingLeft: 2, paddingRight: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Paper sx={{ p: 1, display: "flex", flexDirection: "column" }}>
              <Table size="small" className="gx-info-table" sx={{ mb: 1 }}>
                <TableBody>
                  <TableRow sx={{ height: 45 }}>
                    <TableCell>{CommonConstants.CompanyOffice}</TableCell>
                    <TableCell>{office?.displayName ?? ""}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
              <Tabs
                value={activeTab}
                onChange={handleChangeTab}
                indicatorColor="secondary"
                textColor="inherit"
                variant="fullWidth"
                sx={{ backgroundColor: (theme) => theme.palette.primary.dark, mb: 1, color: "#fff" }}
              >
                <Tab
                  defaultChecked
                  id={ShiftTab}
                  value={ShiftTab}
                  sx={{
                    borderRight: "1px solid #fff",
                    ...(activeTab === ShiftTab && {
                      backgroundColor: (theme) => theme.palette.primary.main,
                    }),
                  }}
                  label={DriverWorkCyclesConstants.DriverWorkShifts}
                />
                <Tab
                  id={CycleTab}
                  value={CycleTab}
                  sx={{
                    borderRight: "1px solid #fff",
                    borderLeft: "1px solid #fff",
                    ...(activeTab === CycleTab && {
                      backgroundColor: (theme) => theme.palette.primary.main,
                    }),
                  }}
                  label={DriverWorkCyclesConstants.DriverWorkCycle}
                />
              </Tabs>
              {activeTab === ShiftTab ? (
                <DriverWorkShiftDetailTab
                  driverWorkShifts={shifts}
                  onClickAdd={handleCreateShift}
                  onClickEdit={handleEditShift}
                />
              ) : (
                <DriverWorkCycleDetailTab
                  driverWorkCycles={cycles}
                  onClickAdd={handleCreateCycle}
                  onClickEdit={handleEditCycle}
                />
              )}
            </Paper>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};
